import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { EMPTY, from } from 'rxjs';
import { catchError, concatMap, finalize, tap, toArray } from 'rxjs/operators';

import { BaseInvoiceFragment, TransactionsAvailableForInvoiceGQL, TransactionsAvailableForInvoiceQueryVariables, UpdateTransactionGQL } from '../../../generated/graphql.generated';
import { JobToolActions } from '../../job/job-tool.actions';
import { remainingBalance } from '../../jobs/jobs.util';
import { DetailsHelperService } from '../../services/details-helper.service';

import { DocumentHelperService } from '../../services/document-helper.service';
import { FreyaHelperService, TransactionWithAvailability } from '../../services/freya-helper.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';

@Component({
  selector: 'app-apply-transaction',
  templateUrl: './apply-transaction.component.html',
  styleUrls: ['./apply-transaction.component.scss', '../../dynamic-dialog-styling.scss']
})
export class ApplyTransactionComponent implements OnInit {

  invoice: BaseInvoiceFragment;

  transactions: TransactionWithAvailability[] = [];

  selectedTransactions: TransactionWithAvailability[] = [];

  saving = false;

  constructor(
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private documentHelper: DocumentHelperService,
    private updateTransactionGQL: UpdateTransactionGQL,
    private localNotify: FreyaNotificationsService,
    private detailsHelper: DetailsHelperService,
    private transactionsAvailableForInvoiceGQL: TransactionsAvailableForInvoiceGQL,
    private freyaHelper: FreyaHelperService,
    private store: Store,
  ) { }

  ngOnInit(): void {
    this.transactions = this.config.data?.transactionsWithAvailability;
    this.invoice = this.config.data?.invoice;
  }

  get remainingInvoiceBalance() {
    return remainingBalance(this.invoice, false);
  }

  get selectedTotal() {
    return this.selectedTransactions.reduce((acc, t) => acc + t.amount, 0);
  }

  get selectionExceedsBalance() {
    return this.selectedTotal > this.remainingInvoiceBalance;
  }

  closeDialog() {
    this.ref.close();
  }

  openCreateTransactionDialog() {
    this.ref.close();
    this.documentHelper.openCreateTransactionsDialogForInvoice(this.invoice);
  }

  applyTransactions() {

    if (!this.selectedTransactions.length || this.selectionExceedsBalance) { return; }

    this.saving = true;

    const refundsFirst = [ ...this.selectedTransactions ].sort((a, b) => a.amount - b.amount);

    let appliedTotal = 0;
    let appliedCount = 0;

    from(refundsFirst).pipe(
      concatMap((transaction) => this.updateTransactionGQL.mutate({
        transactionId: transaction.id,
        invoiceId: this.invoice.id,
      })),
      tap((res) => {
        const transaction = res.data.updateTransaction;

        appliedTotal += transaction.amount;
        appliedCount++;

        this.detailsHelper.pushUpdate({
          type: 'Transactions',
          action: 'update',
          id: transaction.id,
        });

        this.store.dispatch(JobToolActions.applyTransactionSuccess({
          jobId: this.invoice.job.id,
          transaction,
        }));
      }),
      toArray(),
      tap(() => {
        this.localNotify.success(appliedCount === 1 ? 'Transaction applied' : `${appliedCount} transactions applied`);
      }),
      concatMap(() => from(this.checkIfInvoicePaid(appliedTotal))),
      catchError((err) => {
        if (appliedCount === 0) {
          this.localNotify.apolloError('Failed to apply transactions', err);
          return EMPTY;
        }

        if (appliedCount === refundsFirst.length) {
          this.localNotify.apolloError('Transactions applied, but the list could not be refreshed', err);
          this.closeDialog();
          return EMPTY;
        }

        this.localNotify.apolloError(`Applied ${appliedCount} of ${refundsFirst.length} transactions, then failed`, err);

        return from(this.refreshAvailableTransactions()).pipe(catchError(() => EMPTY));
      }),
      finalize(() => {
        this.saving = false;

        if (appliedCount > 0) {
          this.detailsHelper.pushUpdate({
            type: 'Invoice',
            action: 'update',
            id: this.invoice.id,
          });
        }
      }),
    ).subscribe();
  }

  async checkIfInvoicePaid(appliedTotal: number) {

    const remainingBalanceAfterTransaction = this.remainingInvoiceBalance - appliedTotal;

    const invoiceFullyPaid = remainingBalanceAfterTransaction === 0;

    // If the invoice is fully paid, we are done here...
    if (invoiceFullyPaid) {
      this.closeDialog();
      return;
    }

    const { transactionsAvailable } = await this.refreshAvailableTransactions();

    // If there are no more available transactions, close dialog
    if (!transactionsAvailable) {
      this.closeDialog();
    }
  }

  async refreshAvailableTransactions() {

    const vars: TransactionsAvailableForInvoiceQueryVariables = { invoiceId: this.invoice.id, invoiceJobId: this.invoice.job.id };

    const {
      data: {
        invoices: { invoices: [ invoice ] },
        transactions: { transactions },
      },
    } = await this.transactionsAvailableForInvoiceGQL.fetch(vars, { fetchPolicy: 'network-only' }).toPromise();

    const availability = this.freyaHelper.getTransactionsAvailableForInvoice(invoice, transactions);

    this.invoice = invoice;
    this.transactions = availability.transactionsWithAvailability;
    this.selectedTransactions = [];

    return availability;
  }
}
